import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import { useCookies } from "react-cookie";
import { API_URI } from "../../helpers";

const useStyles = makeStyles({
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 180px)",
    minHeight: 500,
  },
  chatHeader: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid #eee",
    gap: 12,
  },
  backBtn: {
    cursor: "pointer",
    fontSize: 22,
    color: "#666",
    padding: "4px 8px",
    "&:hover": { color: "#333" },
  },
  headerAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "#4caf50",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 16,
  },
  headerInfo: {
    flex: 1,
  },
  headerName: {
    fontWeight: 600,
    fontSize: 15,
  },
  headerPhone: {
    fontSize: 12,
    color: "#888",
  },
  statusActions: {
    display: "flex",
    gap: 6,
  },
  statusBtn: {
    padding: "4px 12px",
    fontSize: 11,
    border: "1px solid #ddd",
    borderRadius: 14,
    cursor: "pointer",
    background: "#fff",
    "&:hover": { background: "#f5f5f5" },
  },
  statusBtnActive: {
    padding: "4px 12px",
    fontSize: 11,
    border: "1px solid #4caf50",
    borderRadius: 14,
    cursor: "pointer",
    background: "#4caf50",
    color: "#fff",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px",
    background: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  messageBubble: {
    maxWidth: "65%",
    padding: "8px 14px",
    borderRadius: 12,
    fontSize: 14,
    lineHeight: 1.4,
    wordBreak: "break-word",
    position: "relative",
  },
  msgIncoming: {
    alignSelf: "flex-start",
    background: "#fff",
    borderTopLeftRadius: 4,
    boxShadow: "0 1px 1px rgba(0,0,0,0.06)",
  },
  msgOutgoing: {
    alignSelf: "flex-end",
    background: "#dcf8c6",
    borderTopRightRadius: 4,
    boxShadow: "0 1px 1px rgba(0,0,0,0.06)",
  },
  msgTime: {
    fontSize: 10,
    color: "#999",
    marginTop: 4,
    textAlign: "right",
  },
  msgStatus: {
    fontSize: 10,
    marginLeft: 4,
  },
  msgMedia: {
    fontSize: 12,
    color: "#666",
    fontStyle: "italic",
    marginBottom: 4,
  },
  inputArea: {
    display: "flex",
    padding: "12px 16px",
    borderTop: "1px solid #eee",
    gap: 10,
    background: "#fff",
  },
  textInput: {
    flex: 1,
    padding: "10px 16px",
    border: "1px solid #ddd",
    borderRadius: 24,
    fontSize: 14,
    outline: "none",
    resize: "none",
    fontFamily: "inherit",
    "&:focus": { borderColor: "#4caf50" },
  },
  sendBtn: {
    padding: "10px 24px",
    background: "#4caf50",
    color: "#fff",
    border: "none",
    borderRadius: 24,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    "&:hover": { background: "#43a047" },
    "&:disabled": { background: "#ccc", cursor: "default" },
  },
  loadMoreBtn: {
    alignSelf: "center",
    margin: "8px 0",
  },
  emptyChat: {
    textAlign: "center",
    color: "#999",
    margin: "auto",
    fontSize: 14,
  },
  dateSeparator: {
    alignSelf: "center",
    background: "#e1e5ea",
    color: "#555",
    fontSize: 11,
    padding: "4px 12px",
    borderRadius: 8,
    margin: "8px 0",
  },
});

function formatMsgTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Hoy";
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Ayer";
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function statusIcon(st) {
  switch (st) {
    case "sent":
      return "✓";
    case "delivered":
      return "✓✓";
    case "read":
      return "✓✓";
    case "failed":
      return "✗";
    default:
      return "";
  }
}

export default function WhatsAppChat(props) {
  const classes = useStyles();
  const conversationId = props.match.params.conversationId;
  const [cookies] = useCookies();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const [initialLoad, setInitialLoad] = useState(true);

  const fetchMessages = useCallback(
    (page = 1, append = false) => {
      fetch(API_URI + "/admin/whatsapp/messages/" + conversationId + "?page=" + page)
        .then((r) => r.json())
        .then((res) => {
          if (res.result) {
            setConversation(res.conversation);
            if (append) {
              setMessages((prev) => [...res.messages, ...prev]);
            } else {
              setMessages(res.messages);
            }
            setPagination(res.pagination);
            if (!append) setInitialLoad(false);
          }
        });
    },
    [conversationId]
  );

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Scroll al fondo en carga inicial
  useEffect(() => {
    if (!initialLoad && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView();
    }
  }, [initialLoad, messages.length]);

  // Polling cada 5 segundos
  useEffect(() => {
    const interval = setInterval(() => fetchMessages(), 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const handleSend = () => {
    if (!text.trim() || sending) return;
    setSending(true);

    const params = new FormData();
    params.append("conversation_id", conversationId);
    params.append("message", text.trim());

    fetch(API_URI + "/admin/whatsapp/send", {
      method: "POST",
      body: params,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.result) {
          setMessages((prev) => [...prev, res.message]);
          setText("");
          setTimeout(() => {
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }, 100);
        } else {
          alert("Error al enviar: " + (res.error || "Error desconocido"));
        }
        setSending(false);
      })
      .catch(() => setSending(false));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const changeStatus = (newStatus) => {
    const params = new FormData();
    params.append("id", conversationId);
    params.append("status", newStatus);

    fetch(API_URI + "/admin/whatsapp/status", {
      method: "POST",
      body: params,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.result) {
          setConversation((prev) => ({ ...prev, status: newStatus }));
        }
      });
  };

  const getInitials = (name, phone) => {
    if (name) {
      return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    }
    return phone ? phone.slice(-2) : "?";
  };

  // Agrupar mensajes por fecha
  const renderMessages = () => {
    const elements = [];
    let lastDate = null;

    messages.forEach((msg, i) => {
      const date = formatDate(msg.created_at);
      if (date !== lastDate) {
        elements.push(
          <div key={"date-" + i} className={classes.dateSeparator}>
            {date}
          </div>
        );
        lastDate = date;
      }

      const isOut = msg.direction === "outgoing";
      elements.push(
        <div
          key={msg.id || i}
          className={`${classes.messageBubble} ${
            isOut ? classes.msgOutgoing : classes.msgIncoming
          }`}
        >
          {msg.type !== "text" && msg.type && (
            <div className={classes.msgMedia}>
              📎 {msg.type}
              {msg.media_mime ? ` (${msg.media_mime})` : ""}
            </div>
          )}
          {msg.body && <div>{msg.body}</div>}
          <div className={classes.msgTime}>
            {formatMsgTime(msg.created_at)}
            {isOut && (
              <span
                className={classes.msgStatus}
                style={{ color: msg.status === "read" ? "#53bdeb" : "#999" }}
              >
                {statusIcon(msg.status)}
              </span>
            )}
          </div>
        </div>
      );
    });

    return elements;
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardBody style={{ padding: 0 }}>
            <div className={classes.chatContainer}>
              {/* Header */}
              <div className={classes.chatHeader}>
                <span
                  className={classes.backBtn}
                  onClick={() => props.history.push("/admin/whatsapp")}
                >
                  ←
                </span>
                {conversation && (
                  <>
                    <div className={classes.headerAvatar}>
                      {getInitials(conversation.name, conversation.phone)}
                    </div>
                    <div className={classes.headerInfo}>
                      <div className={classes.headerName}>
                        {conversation.name || conversation.phone}
                      </div>
                      {conversation.name && (
                        <div className={classes.headerPhone}>
                          {conversation.phone}
                        </div>
                      )}
                    </div>
                    <div className={classes.statusActions}>
                      {["open", "closed", "archived"].map((s) => (
                        <span
                          key={s}
                          className={
                            conversation.status === s
                              ? classes.statusBtnActive
                              : classes.statusBtn
                          }
                          onClick={() => changeStatus(s)}
                        >
                          {s === "open"
                            ? "Abierta"
                            : s === "closed"
                            ? "Cerrada"
                            : "Archivada"}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Messages */}
              <div className={classes.messagesArea} ref={messagesAreaRef}>
                {pagination && pagination.current_page < pagination.last_page && (
                  <div className={classes.loadMoreBtn}>
                    <Button
                      size="sm"
                      color="default"
                      onClick={() =>
                        fetchMessages(pagination.current_page + 1, true)
                      }
                    >
                      Cargar mensajes anteriores
                    </Button>
                  </div>
                )}
                {messages.length === 0 && (
                  <div className={classes.emptyChat}>
                    No hay mensajes en esta conversación
                  </div>
                )}
                {renderMessages()}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className={classes.inputArea}>
                <textarea
                  className={classes.textInput}
                  placeholder="Escribí un mensaje..."
                  rows={1}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className={classes.sendBtn}
                  onClick={handleSend}
                  disabled={!text.trim() || sending}
                >
                  {sending ? "..." : "Enviar"}
                </button>
              </div>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
